/**
 * Shared product field templates for different categories
 * Used by ProductModelManager and other admin components
 */

// Cache for templates to avoid repeated API calls
const templateCache = new Map();

/**
 * Get field sections for a given category and optionally subcategory
 * @param {string} categoryName - The name of the category in lowercase
 * @param {number} subcategoryId - Optional subcategory ID for merged templates
 * @returns {Promise<Array>} Promise that resolves to array of field section objects
 */
export const getFieldSections = async (categoryName, subcategoryId = null) => {
  // Create cache key that includes subcategory if provided
  const cacheKey = subcategoryId ? `sub_${subcategoryId}` : `cat_${categoryName}`;
  
  // Check cache first
  if (templateCache.has(cacheKey)) {
    return templateCache.get(cacheKey);
  }

  try {
    let url;
    if (subcategoryId) {
      // Use subcategory-aware endpoint
      url = `/api/product_templates/merged?subcategory_id=${encodeURIComponent(subcategoryId)}`;
    } else if (categoryName) {
      // Fall back to category-only endpoint
      url = `/api/product_templates/${encodeURIComponent(categoryName)}`;
    } else {
      console.log('No category name or subcategory ID provided');
      return [];
    }
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const templateData = await response.json();
    
    // Cache the result
    templateCache.set(cacheKey, templateData);
    
    return templateData;
  } catch (error) {
    console.error(`Failed to fetch template:`, error);
    
    // Return empty array as fallback
    return [];
  }
};

/**
 * Preload templates for better performance
 * @param {Array<string>} categoryNames - Array of category names to preload
 */
export const preloadTemplates = async (categoryNames) => {
  const promises = categoryNames.map(categoryName => getFieldSections(categoryName));
  await Promise.all(promises);
};

/**
 * Clear template cache for a specific item or all items
 * @param {string|number} key - Optional specific cache key (subcategory ID or category name)
 */
export const clearTemplateCache = (key = null) => {
  if (key) {
    // Clear specific entry
    if (typeof key === 'number') {
      templateCache.delete(`sub_${key}`);
    } else {
      templateCache.delete(`cat_${key}`);
    }
  } else {
    // Clear entire cache
    templateCache.clear();
  }
};
